import { forwardRef, type MouseEvent, useState } from 'react'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Button, ListItemText, Menu, MenuItem } from '@mui/material'
import { PlanOrganizationTaskStatus } from '../../../../models/PlanOrganizationTask'
import { AnnualPlanRole } from '../../../../models/AnnualPlanRole'

interface Props {
  currentStatus: PlanOrganizationTaskStatus
  userRole: AnnualPlanRole | null
  onChange: (newStatus: PlanOrganizationTaskStatus) => void
  disabled: boolean
  highlighted?: boolean
  rcnOperations?: boolean
  implementationPhase?: boolean
}

export const PriorityStatusButton = forwardRef<HTMLButtonElement, Props>((props, ref): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  if (props.userRole === null) {
    return <></>
  }

  const getRcnOperationsStatuses = (): PlanOrganizationTaskStatus[] => {
    const statuses = [props.currentStatus]

    switch (props.currentStatus) {
      case PlanOrganizationTaskStatus.NotStarted:
        statuses.push(PlanOrganizationTaskStatus.InProgress)
        break
      case PlanOrganizationTaskStatus.InProgress:
        statuses.push(PlanOrganizationTaskStatus.Accepted)
        break
      case PlanOrganizationTaskStatus.Accepted:
        statuses.push(PlanOrganizationTaskStatus.InProgress)
        break
    }

    return statuses
  }

  const getPlanContributorStatuses = (): PlanOrganizationTaskStatus[] => {
    const statuses: PlanOrganizationTaskStatus[] = []

    switch (props.currentStatus) {
      case PlanOrganizationTaskStatus.NotStarted:
        statuses.push(PlanOrganizationTaskStatus.InProgress)
        break
      case PlanOrganizationTaskStatus.InProgress:
        statuses.push(PlanOrganizationTaskStatus.Submitted)
        break
      case PlanOrganizationTaskStatus.NeedsRevision:
        statuses.push(PlanOrganizationTaskStatus.Submitted)
        break
      case PlanOrganizationTaskStatus.Submitted:
        statuses.push(PlanOrganizationTaskStatus.InProgress)
        break
      default:
        break
    }

    return statuses
  }

  const getPlanAdministratorStatuses = (): PlanOrganizationTaskStatus[] => {
    const statuses: PlanOrganizationTaskStatus[] = []

    switch (props.currentStatus) {
      case PlanOrganizationTaskStatus.NotStarted:
        statuses.push(PlanOrganizationTaskStatus.InProgress)
        break
      case PlanOrganizationTaskStatus.InProgress:
        statuses.push(PlanOrganizationTaskStatus.Submitted)
        break
      case PlanOrganizationTaskStatus.Submitted:
        statuses.push(PlanOrganizationTaskStatus.Accepted)
        statuses.push(PlanOrganizationTaskStatus.NeedsRevision)
        break
      case PlanOrganizationTaskStatus.NeedsRevision:
        statuses.push(PlanOrganizationTaskStatus.Submitted)
        statuses.push(PlanOrganizationTaskStatus.Accepted)
        break
      case PlanOrganizationTaskStatus.Accepted:
        statuses.push(PlanOrganizationTaskStatus.NeedsRevision)
        break
      default: break
    }

    return statuses
  }

  const getContractStatuses = (): PlanOrganizationTaskStatus[] => {
    if (props.rcnOperations === true) return getRcnOperationsStatuses()

    switch (props.userRole) {
      case AnnualPlanRole.PlanContributor: return getPlanContributorStatuses()
      case AnnualPlanRole.PlanAdministrator: return getPlanAdministratorStatuses()
      default: return []
    }
  }

  const getImplementationStatuses = (): PlanOrganizationTaskStatus[] => {
    const statuses: PlanOrganizationTaskStatus[] = []

    switch (props.currentStatus) {
      case PlanOrganizationTaskStatus.UpdatesReady: // task items should be read only
        statuses.push(PlanOrganizationTaskStatus.UpdatesInProgress)
        break
      case PlanOrganizationTaskStatus.UpdatesInProgress:
        statuses.push(PlanOrganizationTaskStatus.UpdatesSubmitted)
        break
      case PlanOrganizationTaskStatus.UpdatesSubmitted: // task items should be read only
        statuses.push(PlanOrganizationTaskStatus.UpdatesInProgress)
        statuses.push(PlanOrganizationTaskStatus.UpdatesNeedsRevision)
        break
      case PlanOrganizationTaskStatus.UpdatesNeedsRevision:
        statuses.push(PlanOrganizationTaskStatus.UpdatesSubmitted)
        break
      // Tracking subphase statuses below
      case PlanOrganizationTaskStatus.TrackingInProgress:
        statuses.push(PlanOrganizationTaskStatus.TrackingCompleted)
        break
      case PlanOrganizationTaskStatus.TrackingCompleted: // task items should be read only
        statuses.push(PlanOrganizationTaskStatus.TrackingInProgress)
        break
    }

    return statuses
  }

  const getAvailableStatuses = (): PlanOrganizationTaskStatus[] => {
    if (props.implementationPhase === true) return getImplementationStatuses()

    return getContractStatuses()
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleStatusChange = (newStatus: PlanOrganizationTaskStatus): void => {
    props.onChange(newStatus)
    handleClose()
  }

  const availableStatuses = getAvailableStatuses()

  return <>
    <Button
      ref={ref}
      data-testid="priorityStatusButton"
      onClick={handleClick}
      disabled={props.disabled}
      sx={{
        padding: '5px 10px',
        backgroundColor: 'var(--grey-50)',
        color: 'var(--grey-500)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: 'fit-content',
        border: '1px solid var(--grey-300)',
        outline: props.highlighted === true ? '-webkit-focus-ring-color auto 1px;' : ''
      }}
    >
      {props.currentStatus}
      <KeyboardArrowDown />
    </Button>

    <Menu
      id="priority-status-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        'aria-labelledby': 'priorityStatusButton'
      }}
    >
      {availableStatuses.length === 0 && <MenuItem disabled>No status options</MenuItem>}
      {availableStatuses.map(status => (
        <MenuItem
          data-testid='statusOption'
          key={status}
          onClick={(event) => {
            event.stopPropagation()
            handleStatusChange(status)
          }}
        >
          <ListItemText>{status}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  </>
})
